import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, Button, TextField, Paper, Grid,
  CircularProgress, Alert, Divider
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import api from "../../../services/api";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" },
  "& .MuiInputLabel-root":    { fontSize: "0.875rem" },
};

export default function CreateAtkItem() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingInit, setLoadingInit] = useState(isEdit);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    unit: "pcs",
    stock: 1,
    min_stock: 0,
    purchase_date: new Date().toISOString().slice(0, 10),
    description: "",
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        try {
          const res = await api.get(`/atk-items/${id}`);
          const data = res.data.data;
          setForm({
            name: data.name || "",
            unit: data.unit || "pcs",
            stock: data.stock || 0,
            min_stock: data.min_stock || 0,
            purchase_date: data.purchase_date || new Date().toISOString().slice(0, 10),
            description: data.description || "",
          });
        } catch (err) {
          setError("Gagal memuat data ATK.");
        } finally {
          setLoadingInit(false);
        }
      };
      fetchItem();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = {
        ...form,
        stock: Number(form.stock),
        min_stock: Number(form.min_stock),
      };

      if (isEdit) {
        await api.put(`/atk-items/${id}`, payload);
      } else {
        await api.post("/atk-items", payload);
      }

      navigate(`/${window.location.pathname.split('/')[1]}/atk/items`);
    } catch (err) {
      setError(err.response?.data?.message || "Gagal menyimpan data ATK.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingInit) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#1677ff" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />} size="small"
          sx={{ textTransform: "none", color: "#595959", "&:hover": { backgroundColor: "#f5f5f5" } }}>
          Kembali
        </Button>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "1.25rem", color: "#1a1a2e" }}>
            {isEdit ? "Edit Stok ATK" : "Tambah Stok ATK"}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#8c8c8c" }}>
            {isEdit ? "Ubah data stok ATK yang sudah ada" : "Input stok alat tulis kantor baru ke gudang"}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: "1px solid #e6ebf1", borderRadius: "10px", p: { xs: 3, md: 4 } }}
        component="form" onSubmit={handleSubmit}>

        {/* Informasi Utama */}
        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mb: 2.5 }}>
          Informasi ATK
        </Typography>
        
        <Grid container spacing={3}>
          {/* Baris 1: Nama ATK Full */}
          <Grid item xs={12}>
            <TextField 
              fullWidth required label="Nama Alat Tulis Kantor" name="name"
              value={form.name} onChange={handleChange} size="small" sx={fieldSx}
              placeholder="Contoh: Kertas HVS A4 80gr, Spidol Snowman Black" 
            />
          </Grid>

          {/* Baris 2: Satuan, Stok, Stok Minimal */}
          <Grid item xs={12} md={4}>
            <TextField 
              fullWidth required label="Satuan" name="unit"
              value={form.unit} onChange={handleChange} size="small" sx={fieldSx}
              placeholder="e.g. rim, pcs, pak" 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField 
              fullWidth required type="number" label="Stok Awal" name="stock"
              value={form.stock} onChange={handleChange} size="small" sx={fieldSx}
              inputProps={{ min: 0 }} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField 
              fullWidth type="number" label="Stok Minimal (Alert)" name="min_stock"
              value={form.min_stock} onChange={handleChange} size="small" sx={fieldSx}
              inputProps={{ min: 0 }}
              helperText="Peringatan jika stok di bawah angka ini"
            />
          </Grid>

          {/* Baris 3: Tanggal & Deskripsi */}
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mb: 2, mt: 1 }}>
              Informasi Pembelian
            </Typography>
            <TextField 
              fullWidth label="Tanggal Pembelian" name="purchase_date"
              type="date" value={form.purchase_date} onChange={handleChange}
              size="small" sx={fieldSx} InputLabelProps={{ shrink: true }} 
            />
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mb: 2, mt: 1 }}>
              Catatan Tambahan
            </Typography>
            <TextField 
              fullWidth label="Deskripsi / Keterangan" name="description"
              value={form.description} onChange={handleChange}
              size="small" multiline rows={2} sx={fieldSx}
              placeholder="Tambahkan info lokasi rak atau detail lainnya..."
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, opacity: 0.6 }} />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button 
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none", color: "#595959", fontWeight: 500 }}
          >
            Batal
          </Button>
          <Button 
            type="submit" variant="contained" disabled={loading}
            sx={{
              textTransform: "none", borderRadius: "8px", px: 4,
              backgroundColor: "#1677ff", fontWeight: 600,
              "&:hover": { backgroundColor: "#0958d9" },
              minWidth: 150,
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : (isEdit ? "Simpan Perubahan" : "Simpan Stok ATK")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
